import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowForward from '@mui/icons-material/ArrowForward';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import { CDN_BASE_URL } from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { buildExamOverviewPath, buildMockExamsPath, buildPracticePath } from '../utils/contentRoutes';
import { CONTENT_MAX_WIDTH, accentRuleSx, ctaPrimarySx, ctaSecondarySx, ctaTertiarySx } from '../styles/tokens';
import { INK, MUTED, SURFACE } from '../pages/Practice/map/mapTokens';
import LanguageSelectionSection from './LanguageSelectionSection';
import { HERO } from './home/homeContent';

const sliderImages = [
  `${CDN_BASE_URL}/situations/img_1.png?width=240&height=420`,
  `${CDN_BASE_URL}/situations/img_2.png?width=240&height=420`,
  `${CDN_BASE_URL}/situations/img_3.png?width=240&height=420`,
  `${CDN_BASE_URL}/situations/img_4.png?width=240&height=420`,
  `${CDN_BASE_URL}/situations/img_5.png?width=240&height=420`,
];

const desktopSliderPositions = {
  2: { x: -190, y: 50, scale: 0.55, zIndex: 1, opacity: 0.35 },
  1: { x: -110, y: 20, scale: 0.8, zIndex: 2, opacity: 0.75 },
  0: { x: 0, y: 0, scale: 1, zIndex: 3, opacity: 1 },
  '-1': { x: 110, y: 20, scale: 0.8, zIndex: 2, opacity: 0.75 },
  '-2': { x: 190, y: 50, scale: 0.55, zIndex: 1, opacity: 0.35 },
};

const mobileSliderPositions = {
  2: { x: -120, y: 35, scale: 0.55, zIndex: 1, opacity: 0.3 },
  1: { x: -70, y: 12, scale: 0.75, zIndex: 2, opacity: 0.6 },
  0: { x: 0, y: 0, scale: 1, zIndex: 3, opacity: 1 },
  '-1': { x: 70, y: 12, scale: 0.75, zIndex: 2, opacity: 0.6 },
  '-2': { x: 120, y: 35, scale: 0.55, zIndex: 1, opacity: 0.3 },
};

const HERO_SLIDES_IDLE_TIMEOUT_MS = 1500;
/** Was 2.5s, which advanced faster than you could look at a slide. */
const SLIDE_INTERVAL_MS = 5000;

const Hero = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [hasLoadedSlides, setHasLoadedSlides] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);
  const advanceFrameRef = React.useRef(null);

  const advanceSlide = React.useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % sliderImages.length);
  }, []);

  React.useEffect(() => {
    // A carousel that moves on its own with no way to stop it is hostile to
    // anyone reading slowly, and to anyone who has asked for reduced motion.
    if (!hasLoadedSlides || isPaused || prefersReducedMotion) {
      return undefined;
    }

    const timer = setTimeout(() => {
      advanceSlide();
    }, SLIDE_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [advanceSlide, currentIndex, hasLoadedSlides, isPaused, prefersReducedMotion]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let idleHandle = null;
    let timeoutHandle = null;

    const loadRemainingSlides = () => {
      setHasLoadedSlides(true);
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(loadRemainingSlides, { timeout: HERO_SLIDES_IDLE_TIMEOUT_MS });
    } else {
      timeoutHandle = window.setTimeout(loadRemainingSlides, 0);
    }

    return () => {
      if (typeof window.cancelIdleCallback === 'function' && idleHandle !== null) {
        window.cancelIdleCallback(idleHandle);
      }

      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, []);

  React.useEffect(
    () => () => {
      if (advanceFrameRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(advanceFrameRef.current);
      }
    },
    [],
  );

  const sliderPositions = isMobile ? mobileSliderPositions : desktopSliderPositions;
  const maxOffset = Math.floor(sliderImages.length / 2);
  const slideIndices = hasLoadedSlides ? sliderImages.map((_, index) => index) : [currentIndex];

  const handleAdvanceSlide = React.useCallback(() => {
    if (hasLoadedSlides) {
      advanceSlide();
      return;
    }

    setHasLoadedSlides(true);

    if (typeof window !== 'undefined') {
      advanceFrameRef.current = window.requestAnimationFrame(() => {
        advanceFrameRef.current = null;
        advanceSlide();
      });
      return;
    }

    advanceSlide();
  }, [advanceSlide, hasLoadedSlides]);

  return (
    <Box
      component="section"
      sx={{
        bgcolor: SURFACE,
        px: { xs: 2, sm: 4, md: 8 },
        pt: { xs: 5, md: 7 },
        pb: { xs: 5, md: 7 },
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Grid areas rather than a flex row: on mobile the carousel belongs
          between the language picker and the buttons — it shows what you are
          about to practise, so it should be seen before the decision, not after
          it — while on desktop it stays a full-height right-hand column. Flex
          cannot interleave two columns like that without duplicating the DOM. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateAreas: { xs: '"intro" "media" "actions"', md: '"intro media" "actions media"' },
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
          gridTemplateRows: { md: 'auto auto' },
          alignItems: 'center',
          columnGap: { md: 6 },
          rowGap: { xs: 4, md: 0 },
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            gridArea: 'intro',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
            alignSelf: { md: 'end' },
          }}
        >
          {/* Decorative greeting. Deliberately not a heading: the h1 below is the
              page's real title, and it used to be the small one. */}
          <Typography
            translate="no"
            className="notranslate"
            lang={language === 'de' ? 'de' : 'fr'}
            component="p"
            sx={{ color: MUTED, fontWeight: 600, fontSize: '1rem', mb: 1.5, letterSpacing: 0.3 }}
          >
            {language === 'de' ? HERO.eyebrow.de : HERO.eyebrow.fr}
          </Typography>

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 900,
              width: { xs: '100%', md: 'auto' },
              color: 'primary.main',
              fontSize: { xs: 'clamp(1.35rem, 6.4vw, 2.2rem)', md: '2.65rem' },
              lineHeight: 1.12,
              letterSpacing: -0.5,
              mb: 2,
            }}
          >
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {HERO.title.firstLine}
            </Box>
            <br /> {HERO.title.secondLine}
          </Typography>

          <Box aria-hidden sx={{ ...accentRuleSx, width: 56, mb: 2.5, mx: { xs: 'auto', md: 0 } }} />

          <Typography sx={{ color: INK, fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7, mb: 3 }}>
            {HERO.subtitle}
          </Typography>

          {/* The primary CTA routes to /{language}/practice, so the choice belongs
              here — ahead of the click — rather than in its own section further
              down the page where it used to sit. */}
          <Box sx={{ width: '100%', maxWidth: 420, mb: 3 }}>
            <Typography
              component="p"
              sx={{ color: MUTED, fontWeight: 700, fontSize: '0.8125rem', mb: 1, textTransform: 'uppercase' }}
            >
              I am learning
            </Typography>
            <LanguageSelectionSection compact variant="segmented" />
          </Box>
        </Box>

        <Box
          sx={{
            gridArea: 'media',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Box
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box
              onClick={handleAdvanceSlide}
              role="button"
              aria-label="Show the next example situation"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleAdvanceSlide();
                }
              }}
              sx={{
                position: 'relative',
                width: { xs: 280, sm: 340, md: 500 },
                height: { xs: 210, sm: 240, md: 340 },
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '4px' },
              }}
            >
              {slideIndices.map((imageIndex) => {
                const imageSrc = sliderImages[imageIndex];
                let offset = imageIndex - currentIndex;

                if (offset > maxOffset) {
                  offset -= sliderImages.length;
                } else if (offset < -maxOffset) {
                  offset += sliderImages.length;
                }

                const position = sliderPositions[offset] ?? {
                  x: offset > 0 ? 240 : -240,
                  y: 80,
                  scale: 0.4,
                  zIndex: 0,
                  opacity: 0,
                };

                const isActive = offset === 0;

                return (
                  <Box
                    key={`slider-img-${imageIndex}`}
                    component="img"
                    src={imageSrc}
                    alt={isActive ? `Everyday situation you practise for the fide test` : ''}
                    aria-hidden={isActive ? undefined : true}
                    loading={isActive ? 'eager' : 'lazy'}
                    fetchPriority={isActive ? 'high' : undefined}
                    width="240"
                    height="420"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: isMobile ? 170 : 240,
                      height: isMobile ? 220 : 300,
                      objectFit: 'cover',
                      borderRadius: 3,
                      border: '4px solid #FFFFFF',
                      boxShadow: '0 16px 40px rgba(19, 62, 135, 0.18)',
                      backgroundColor: 'background.paper',
                      transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${position.scale})`,
                      transition: 'transform 0.6s ease, opacity 0.6s ease',
                      opacity: position.opacity,
                      zIndex: position.zIndex,
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  />
                );
              })}
            </Box>

            {/* Indicators: without them the carousel gives no sense of how much
                there is, and no way to reach a slide you just missed. */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {sliderImages.map((_, index) => (
                <Box
                  key={`slider-dot-${index}`}
                  component="button"
                  type="button"
                  aria-label={`Show situation ${index + 1} of ${sliderImages.length}`}
                  aria-current={index === currentIndex ? 'true' : undefined}
                  onClick={() => {
                    setHasLoadedSlides(true);
                    setCurrentIndex(index);
                  }}
                  sx={{
                    width: index === currentIndex ? 22 : 8,
                    height: 8,
                    p: 0,
                    border: 'none',
                    borderRadius: 99,
                    cursor: 'pointer',
                    bgcolor: index === currentIndex ? 'primary.main' : '#C9D6E8',
                    transition: 'width .25s ease, background-color .25s ease',
                    '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '2px' },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
        {/* Placed after `media` in the DOM as well as in the mobile grid: CSS
            grid can reorder visually, but a screen reader follows source order,
            and a sighted user seeing the carousel before the buttons while a
            screen-reader user hears them the other way round is exactly the
            mismatch WCAG's meaningful-sequence rule is about. */}
        <Box
          sx={{
            gridArea: 'actions',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
            alignSelf: { md: 'start' },
            mt: { md: 3 },
          }}
        >
          {/* The claim comes before the button: this is the reason to click, and
              it used to sit underneath the thing it was meant to justify. */}
          <Box
            sx={{
              bgcolor: '#E8F5EE',
              border: '1px solid #C6E6D4',
              borderRadius: '12px',
              px: 2,
              py: 1.25,
              mb: 3,
              maxWidth: 520,
            }}
          >
            <Typography sx={{ color: '#1B7A47', fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.5 }}>
              {HERO.freeLine}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.875rem', lineHeight: 1.5, mt: 0.25 }}>
              {HERO.freeLineSub}
            </Typography>
          </Box>

          {/* Two ways in, one weight each: practise (loud, filled) and the free mock
              test (outlined — the same size, so it is a real alternative rather than
              an afterthought). The exam-structure link is mobile-only: on a phone it
              is the third item of a single stack you scroll past anyway, while on
              desktop it would be a third competing target next to the two real
              entry points. Desktop visitors still reach that page from the navbar
              and from the exam section further down. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { sm: 'wrap' },
              alignItems: { xs: 'stretch', sm: 'center' },
              columnGap: { sm: 2 },
              rowGap: 1.5,
              width: { xs: '100%', sm: 'auto' },
              mb: 2.5,
            }}
          >
            <Button
              component={RouterLink}
              to={buildPracticePath(language)}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={ctaPrimarySx}
            >
              {HERO.primaryCta}
            </Button>
            <Button
              component={RouterLink}
              to={buildMockExamsPath(language, 'oral', { explicit: true })}
              size="large"
              startIcon={<TimerOutlined />}
              endIcon={<ArrowForward />}
              sx={{
                ...ctaSecondarySx,
                minWidth: { sm: 220 },
                // The arrow pairs this with the primary button in the mobile stack,
                // where both are full-width rows; side by side on desktop the two
                // arrows just repeat, so it is dropped there.
                '& .MuiButton-endIcon': { display: { xs: 'inline-flex', sm: 'none' } },
              }}
            >
              {language === 'de'
                ? HERO.mockExamCta.de.split(/(Modelltest)/).map((part, index) =>
                    part === 'Modelltest' ? (
                      <span key={index} translate="no" className="notranslate" lang="de">
                        {part}
                      </span>
                    ) : (
                      part
                    ),
                  )
                : HERO.mockExamCta.fr}
            </Button>
            {/* Hidden with CSS rather than by a `useMediaQuery` branch: the site is
                prerendered, so a JS-decided breakpoint would bake one viewport's
                answer into the served HTML. */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
              }}
            >
              <Button
                component={RouterLink}
                to={buildExamOverviewPath(language)}
                endIcon={<ArrowForward />}
                sx={ctaTertiarySx}
              >
                {HERO.secondaryCta}
              </Button>
            </Box>
          </Box>

          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent={{ xs: 'center', md: 'flex-start' }}
            sx={{ gap: 1, rowGap: 0.75 }}
          >
            {HERO.trust.map((item, index) => (
              <React.Fragment key={item}>
                {index > 0 ? (
                  <Box
                    aria-hidden
                    sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#C9D6E8', alignSelf: 'center' }}
                  />
                ) : null}
                <Typography
                  component="span"
                  sx={{ color: MUTED, fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {item}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
